import type { FormInputsModel, UseCreditCardFormOptions } from './creditCardFormTypes.ts';

const CHECKOUT_SYSTEM_FONT_STACK
  = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif';

export function useCardFormState(options: Pick<UseCreditCardFormOptions, 'withEmail'>) {
  const {
    $paymentMethods,
  } = useNuxtApp();
  const { openError } = useAllModals();
  const { gatewayType } = useHelpers();
  const { track } = useEventsDispatcher();

  let email: Ref<string> | undefined;
  let showInputError: Ref<boolean> | undefined;
  let isValidEmail: Ref<boolean> | undefined;
  let validateEmailFn: ((withRedirect?: boolean) => Promise<boolean>) | undefined;
  let onInputEmail: ((value: string) => void) | undefined;

  const baseFields: FormInputsModel = { number: null, expiry: null, cvv: null, name: null };
  const CARD_FIELDS = ['number', 'expiry', 'cvv'] as const;
  const errors = ref<FormInputsModel>(
    options.withEmail ? { ...baseFields, email: null } : { ...baseFields },
  );
  const isEmpty = ref<FormInputsModel>(
    options.withEmail ? { ...baseFields, email: null } : { ...baseFields },
  );

  if (options.withEmail) {
    const emailComposable = useEmail();
    email = emailComposable.email;
    showInputError = emailComposable.showInputError;
    validateEmailFn = emailComposable.validateEmail;
    const checkEmailFormat = emailComposable.checkEmailFormat;
    isValidEmail = ref(false);

    onInputEmail = (value: string): void => {
      if (value.length < 2) {
        isEmpty.value.email = true;
      } else {
        isEmpty.value.email = false;
        resetErrorByName('email');
      }
    };

    watch(email, () => {
      showInputError!.value = false;
      isValidEmail!.value = checkEmailFormat(email!.value);
    });
  }

  const cardRef = ref();
  const paymentComponent = ref<any>(null);
  const nameOnCard = ref('');
  const isStartedEventSended = ref(false);

  const gatewayStore = useKegelGatewayStore();
  const isRound = computed(() => gatewayStore.provider === 'round');
  const {
    isCheckoutVariantC,
    isCheckoutVariantD,
    isCheckoutVariantE,
  } = useCheckoutFormVariant();
  const isWhiteningCheckoutVariant = computed(() =>
    isCheckoutVariantC.value || isCheckoutVariantD.value || isCheckoutVariantE.value,
  );
  const fonts = computed(() => {
    if (isRound.value || isWhiteningCheckoutVariant.value) {
      return [];
    }

    return [
      {
        src: '@/assets/fonts/SF-Pro-Text-Semibold.woff2',
        fontFamily: ['Roboto', 'sans-serif'],
        fontWeight: '600',
      },
      'https://fonts.googleapis.com/css?family=Roboto:300,500,600',
    ];
  });
  const styles = computed(() => ({
    base: {
      'fontSize': '16px',
      'fontWeight': isRound.value || isWhiteningCheckoutVariant.value ? 400 : 600,
      'fontFamily': isRound.value || isWhiteningCheckoutVariant.value
        ? CHECKOUT_SYSTEM_FONT_STACK
        : ['Roboto', 'sans-serif'],
      '::placeholder': {
        color: isWhiteningCheckoutVariant.value ? '#6B6B73' : '#1111137F',
      },
    },
  }));

  function onReady(event: any): void {
    paymentComponent.value = event;
  }

  function sendStartedEvent() {
    if (isStartedEventSended.value) return;

    track('Check-out Started', {
      type: $paymentMethods.card,
      gateway: gatewayType.value,
    });

    isStartedEventSended.value = true;
  }

  function onChange(event: any): void {
    const { field } = event;
    if (event.empty !== undefined) {
      isEmpty.value[field] = event.empty;
      if (!event.empty) sendStartedEvent();
    }
    if (event.error !== undefined) {
      errors.value[field] = event.error || null;
    }
  }

  /**
   * The Hyperswitch element reports one aggregate state for the whole card, so the three card
   * fields move together. Keeping them locked makes every state below this valid by construction.
   */
  function onHyperswitchChange(
    event: { empty: boolean; complete: boolean; error: string | null; paymentMethod?: string | null },
  ): void {
    if (event.paymentMethod && event.paymentMethod !== 'card') return;

    for (const field of CARD_FIELDS) {
      isEmpty.value[field] = !event.complete;
      errors.value[field] = null;
    }
    if (event.error) errors.value[CARD_FIELDS[0]] = event.error;
  }

  function resetCardFields(): void {
    for (const field of CARD_FIELDS) {
      isEmpty.value[field] = null;
      errors.value[field] = null;
    }
  }

  function resetErrorByName(fieldName: string): void {
    errors.value[fieldName] = null;
  }

  function onFieldChange(fieldName: string, event?: { error?: unknown }): void {
    if (isRound.value && event?.error) return;

    resetErrorByName(fieldName);
  }

  function onInputName(): void {
    const regexp = /^[A-Z'`~.•-]{2,}( [A-Z'`~.•-]{2,})*$/i;
    errors.value.name = !regexp.test(nameOnCard.value);
    const trimName = nameOnCard.value.trim();
    if (trimName.length < 2) {
      nameOnCard.value = trimName;
      isEmpty.value.name = true;
    } else {
      isEmpty.value.name = false;
    }
  }

  const areFieldsFilled = computed(() =>
    Object.values(isEmpty.value).every(field => field === false),
  );

  const isDisabledButton = computed(() => {
    if (options.withEmail && (!isValidEmail?.value || showInputError?.value)) return true;
    return Object.values(errors.value).some(Boolean) || !areFieldsFilled.value;
  });

  function isFormFilled(): boolean {
    const invalidFields = isRound.value
      ? Object.keys(errors.value).filter(key => Boolean(errors.value[key]))
      : [];
    const emailValid = !options.withEmail || isValidEmail?.value;
    if (areFieldsFilled.value && emailValid && !invalidFields.length) return true;

    Object.keys(isEmpty.value).forEach((key) => {
      errors.value[key] = isEmpty.value[key] || isEmpty.value[key] === null || errors.value[key];
    });

    if (options.withEmail) {
      const otherFieldsFilled = Object.keys(isEmpty.value)
        .filter(key => key !== 'email')
        .every(key => isEmpty.value[key] === false);
      const hasCardErrors = invalidFields.some(key => key !== 'email');
      openError({
        content: !isEmpty.value.email && otherFieldsFilled && !hasCardErrors
          ? 'mail_error_message'
          : 'lp_v7_pop_up_payment_error_message_2',
      });
    } else {
      openError({ content: 'lp_v7_pop_up_payment_error_message_2' });
    }

    return false;
  }

  watch(nameOnCard, (newValue: string, oldValue: string) => {
    if (newValue.length < oldValue.length) resetErrorByName('name');
  });

  return {
    cardRef,
    paymentComponent,

    nameOnCard,
    errors,

    fonts,
    styles,

    areFieldsFilled,
    isDisabledButton,

    onReady,
    onHyperswitchChange,
    resetCardFields,
    sendStartedEvent,
    onChange,
    onFieldChange,
    onInputName,
    isFormFilled,

    email,
    showInputError,
    isValidEmail,
    validateEmailFn,
    onInputEmail,
  };
}
